import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  Theme,
  Divider,
  ButtonGroup,
  Button
} from '@mui/material';
import { deepEqual } from 'fast-equals';
import { Add, Close, Save } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Field, Formik, Form } from 'formik';
import React, { FC, useMemo } from 'react';
import * as Yup from 'yup';
import {
  IAttorneyOffice,
  IPeople,
  INeighborhoodDetailSalesAndContractsInfo,
  INeighborhoodDetailSalesAndContractsPut,
  IResponse
} from '../../../../models';
import { PhotoOwnerType } from '../../../../models/photo-owner-types';
import { formatMoney, decimalRegExp } from '../../../../helpers';
import { SalesAndContractsImages } from './sales-and-contracts-images';
import { ImageUploader, Loader, SMBSelect } from '../../../../components';
import {
  putNeighborhoodSalesAndContractInfo,
  getAdminAttorneyOffices,
  getPhotoList
} from '../../../../fetch';
import { getCommonPeople } from '../../../../fetch/lookups';

interface ISalesAndContracts {
  salesAndContractsInfo: INeighborhoodDetailSalesAndContractsInfo;
  isLoadingSalesInfo: boolean;
  refetchSalesAndContractsInfo: () => void;
}

const internalCommissionRates = [
  {
    value: 4,
    label: '4%'
  },
  {
    value: 4.25,
    label: '4.25%'
  },
  {
    value: 4.5,
    label: '4.5%'
  },
  {
    value: 4.75,
    label: '4.75%'
  },
  {
    value: 5,
    label: '5%'
  },
  {
    value: 5.25,
    label: '5.25%'
  },
  {
    value: 5.5,
    label: '5.5%'
  },
  {
    value: 5.75,
    label: '5.75%'
  },
  {
    value: 6,
    label: '6%'
  }
];

const agentRates = [
  {
    value: 1.5,
    label: '1.5%'
  },
  {
    value: 1.75,
    label: '1.75%'
  },
  {
    value: 2,
    label: '2%'
  },
  {
    value: 2.25,
    label: '2.25%'
  },
  {
    value: 2.5,
    label: '2.5%'
  },
  {
    value: 2.75,
    label: '2.75%'
  },
  {
    value: 3,
    label: '3%'
  },
  {
    value: 3.25,
    label: '3.25%'
  },
  {
    value: 3.5,
    label: '3.5%'
  },
  {
    value: 3.75,
    label: '3.75%'
  }
];

const SalesAndContractsSchema = Yup.object().shape({
  neighborhoodId: Yup.string(),
  priceRangeSlogan: Yup.string(),
  preferedLenderClosingCostContribution: Yup.string(),
  approvedLenderClosingCostContribution: Yup.string(),
  inHouseCommissionRate: Yup.number().min(4).max(6).required(),
  coopInternalCommissionRate: Yup.number().min(1.5).max(3.75).required(),
  coopExternalCommissionRate: Yup.number().min(1.5).max(3.75).required(),
  salesDriver: Yup.string(),
  percentMarkUp: Yup.string().min(1).required('Target Margin is required.'),
  appraisalAlarmLevel: Yup.string().required('Appraisal Alert Level is required'),
  additionalUpgradeCap: Yup.string(),
  closingAttorneyId: Yup.string().required('Closing Attorney is required.'),
  compactionPersonId: Yup.string().required('Compaction User is required.')
});

export const SalesAndContracts: FC<ISalesAndContracts> = ({
  salesAndContractsInfo,
  isLoadingSalesInfo,
  refetchSalesAndContractsInfo
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { neighborhoodId }: { neighborhoodId: string } = useParams();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const history = useHistory();

  const { data: closingAttorneyIds } = useQuery<IAttorneyOffice[], Error>(
    ['getAdminAttorneyOffices'],
    () => getAdminAttorneyOffices()
  );

  const { data: peopleRes } = useQuery<IResponse<IPeople[]>, Error>(
    ['fetchCompactionTestUsers'],
    () => getCommonPeople({ personType: 'Custom', customRole: 'BL_Compaction' })
  );

  const { data: platMaps } = useQuery<any, Error>(['getPlatMapsList'], () =>
    getPhotoList(neighborhoodId, 'NeighborhoodPlat')
  );

  const { data: logos, refetch: refetchLogos } = useQuery<any, Error>(['getLogosList'], () =>
    getPhotoList(neighborhoodId, 'NeighborhoodLogo')
  );

  const { data: actionPhotographs, refetch: refetchActionPhotographs } = useQuery<any, Error>(
    ['getActionPhotoList'],
    () => getPhotoList(neighborhoodId, 'NeighborhoodAction')
  );

  const logoGuids = logos?.map(str => str.split('photo/')[1]);
  const actionPhotoGuids = actionPhotographs?.map(str => str.split('photo/')[1]);

  const compactionPersonIds = useMemo(() => peopleRes?.records || [], [peopleRes]);

  return (
    <>
      {isLoadingSalesInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingSalesInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            neighborhoodId,
            priceRangeSlogan: salesAndContractsInfo?.priceRangeSlogan ?? '',
            preferredLenderClosingCostContribution:
              salesAndContractsInfo?.preferredLenderClosingCostContribution
                ? formatMoney(salesAndContractsInfo.preferredLenderClosingCostContribution, 2)
                : '',
            approvedLenderClosingCostContribution:
              salesAndContractsInfo?.approvedLenderClosingCostContribution
                ? formatMoney(salesAndContractsInfo.approvedLenderClosingCostContribution, 2)
                : '',
            coopInternalCommissionRate:
              salesAndContractsInfo?.coopInternalCommissionRate > 0
                ? salesAndContractsInfo?.coopInternalCommissionRate
                : 3,
            coopExternalCommissionRate:
              salesAndContractsInfo?.coopExternalCommissionRate > 0
                ? salesAndContractsInfo?.coopExternalCommissionRate
                : 2,
            salesDriver: salesAndContractsInfo?.salesDriver
              ? salesAndContractsInfo.salesDriver
              : 'House',
            percentMarkUp: salesAndContractsInfo?.percentMarkUp
              ? `${salesAndContractsInfo.percentMarkUp}%`
              : '',
            appraisalAlarmLevel: salesAndContractsInfo?.appraisalAlarmLevel ?? 'Medium',
            inHouseCommissionRate:
              salesAndContractsInfo?.internalCommissionRate > 0
                ? salesAndContractsInfo?.internalCommissionRate
                : 5.5,
            additionalUpgradeCap: !isNaN(salesAndContractsInfo?.additionalUpgradeCap)
              ? formatMoney(salesAndContractsInfo?.additionalUpgradeCap)
              : '',
            closingAttorneyId: salesAndContractsInfo?.closingAttorneyId ?? '',
            compactionPersonId: salesAndContractsInfo?.compactionPersonId ?? '',
            platMaps: platMaps ?? [],
            logos: logos ?? [],
            actionPhotographs: actionPhotographs ?? [],
            logoGuids: logoGuids ?? [],
            actionPhotoGuids: actionPhotoGuids ?? []
          }}
          validationSchema={SalesAndContractsSchema}
          onSubmit={async (values, actions) => {
            if (neighborhoodId !== 'add') {
              try {
                const data: INeighborhoodDetailSalesAndContractsPut = {
                  ...values,
                  percentMarkUp: values.percentMarkUp
                    ? Number(values.percentMarkUp.replace('%', ''))
                    : null,
                  approvedLenderClosingCostContribution:
                    values.approvedLenderClosingCostContribution
                      ? Number(
                          values.approvedLenderClosingCostContribution
                            .replace('$', '')
                            .replaceAll(',', '')
                        )
                      : null,
                  preferredLenderClosingCostContribution:
                    values.preferredLenderClosingCostContribution
                      ? Number(
                          values.preferredLenderClosingCostContribution
                            .replace('$', '')
                            .replaceAll(',', '')
                        )
                      : null,
                  inHouseCommissionRate: values.inHouseCommissionRate
                    ? Number(values.inHouseCommissionRate)
                    : null,
                  additionalUpgradeCap: values.additionalUpgradeCap
                    ? Number(
                        (values.additionalUpgradeCap as string).replace('$', '').replaceAll(',', '')
                      )
                    : null,
                  closingAttorneyId: values.closingAttorneyId
                    ? Number(values.closingAttorneyId)
                    : null,
                  compactionPersonId: values.compactionPersonId
                    ? Number(values.compactionPersonId)
                    : null
                };
                await putNeighborhoodSalesAndContractInfo(neighborhoodId, data);
                await refetchSalesAndContractsInfo();
                enqueueSnackbar('Neighborhood has been updated successfully!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? 'Something went wrong. Neighborhood was not updated.',
                  {
                    variant: 'error'
                  }
                );
              }
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Box mt={2} ml={1} mr={1}>
                  <Grid container alignItems="flex-start" justifyContent="center" spacing={1}>
                    <Grid item xs={12}>
                      <Field name={`priceRangeSlogan`}>
                        {({ field }) => {
                          return (
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              variant="outlined"
                              autoComplete="nope"
                              label="Price Range Slogan"
                              name="priceRangeSlogan"
                              size="small"
                              value={values.priceRangeSlogan}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('priceRangeSlogan', e.target.value)}
                              error={Boolean(touched.priceRangeSlogan && errors.priceRangeSlogan)}
                              helperText={touched.priceRangeSlogan && errors.priceRangeSlogan}
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`preferredLenderClosingCostContribution`}>
                        {({ field }) => {
                          return (
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Perfered Lender Closing Cost Contribution"
                              name="preferredLenderClosingCostContribution"
                              size="small"
                              value={values.preferredLenderClosingCostContribution}
                              onBlur={e => {
                                handleBlur(e);
                                setFieldValue(
                                  'preferredLenderClosingCostContribution',
                                  formatMoney(e.target.value, 2)
                                );
                              }}
                              onChange={e =>
                                setFieldValue(
                                  'preferredLenderClosingCostContribution',
                                  e.target.value
                                )
                              }
                              error={Boolean(
                                touched.preferredLenderClosingCostContribution &&
                                  errors.preferredLenderClosingCostContribution
                              )}
                              helperText={
                                touched.preferredLenderClosingCostContribution &&
                                errors.preferredLenderClosingCostContribution
                              }
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`approvedLenderClosingCostContribution`}>
                        {({ field }) => {
                          return (
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Approved Lender Closing Cost Contribution"
                              name="approvedLenderClosingCostContribution"
                              size="small"
                              value={values.approvedLenderClosingCostContribution}
                              onBlur={e => {
                                handleBlur(e);
                                setFieldValue(
                                  'approvedLenderClosingCostContribution',
                                  formatMoney(e.target.value, 2)
                                );
                              }}
                              onChange={e =>
                                setFieldValue(
                                  'approvedLenderClosingCostContribution',
                                  e.target.value
                                )
                              }
                              error={Boolean(
                                touched.approvedLenderClosingCostContribution &&
                                  errors.approvedLenderClosingCostContribution
                              )}
                              helperText={
                                touched.approvedLenderClosingCostContribution &&
                                errors.approvedLenderClosingCostContribution
                              }
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Field name={`internalCommissionRate`}>
                        {({ field }) => {
                          return (
                            <>
                              <Typography
                                className={
                                  isMobile ? classes.internalCommissionRateTitle : classes.emphasis
                                }
                                variant={'subtitle1'}
                              >
                                In House Commission Rate
                              </Typography>
                              <Box className={isMobile ? classes.sliderContainer : null}>
                                <Slider
                                  aria-label="In House Commission Rate"
                                  name="internalCommissionRate"
                                  min={4}
                                  max={6}
                                  step={0.25}
                                  defaultValue={6}
                                  valueLabelDisplay="off"
                                  orientation={isMobile ? 'vertical' : 'horizontal'}
                                  marks={internalCommissionRates}
                                  value={values.inHouseCommissionRate}
                                  onChange={(e, value) =>
                                    setFieldValue('inHouseCommissionRate', value)
                                  }
                                />
                              </Box>
                            </>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Typography className={classes.emphasis} variant={'subtitle1'}>
                        Coop Commission Rates
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Field name={`coopInternalCommissionRate`}>
                        {({ field }) => {
                          return (
                            <>
                              <Typography
                                className={isMobile ? classes.mobileAgentTitle : null}
                                variant={'subtitle2'}
                              >
                                Our Agent
                              </Typography>
                              <Box className={isMobile ? classes.sliderContainer : null}>
                                <Slider
                                  aria-label="Our Agent"
                                  name="coopInternalCommissionRate"
                                  min={1.5}
                                  max={3.75}
                                  step={0.25}
                                  defaultValue={3}
                                  valueLabelDisplay="off"
                                  orientation={isMobile ? 'vertical' : 'horizontal'}
                                  marks={agentRates}
                                  value={values.coopInternalCommissionRate}
                                  onChange={(e, value) =>
                                    setFieldValue('coopInternalCommissionRate', value)
                                  }
                                />
                              </Box>
                            </>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Field name={`coopExternalCommissionRate`}>
                        {({ field }) => {
                          return (
                            <>
                              <Typography
                                className={isMobile ? classes.mobileAgentTitle : null}
                                variant={'subtitle2'}
                              >
                                Their Agent
                              </Typography>
                              <Box className={isMobile ? classes.sliderContainer : null}>
                                <Slider
                                  aria-label="Their Agent"
                                  name="coopExternalCommissionRate"
                                  min={1.5}
                                  max={3.75}
                                  step={0.25}
                                  defaultValue={1.5}
                                  valueLabelDisplay="off"
                                  orientation={isMobile ? 'vertical' : 'horizontal'}
                                  marks={agentRates}
                                  value={values.coopExternalCommissionRate}
                                  onChange={(e, value) =>
                                    setFieldValue('coopExternalCommissionRate', value)
                                  }
                                />
                              </Box>
                            </>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`salesDriver`}>
                        {({ field }) => {
                          return (
                            <FormControl component="fieldset" required>
                              <FormLabel className={classes.emphasis}>Sales Driver</FormLabel>
                              <RadioGroup
                                row
                                aria-label="salesDriver"
                                name="salesDriver"
                                value={values.salesDriver}
                                onChange={(e, value) =>
                                  setFieldValue('salesDriver', e.target.value)
                                }
                              >
                                <FormControlLabel value="House" control={<Radio />} label="House" />
                                <FormControlLabel
                                  value="Location"
                                  control={<Radio />}
                                  label="Location"
                                />
                              </RadioGroup>
                            </FormControl>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`percentMarkUp`}>
                        {({ field }) => {
                          return (
                            <TextField
                              {...field}
                              fullWidth
                              required
                              variant="outlined"
                              autoComplete="nope"
                              label="Target Margin"
                              name="percentMarkUp"
                              size="small"
                              value={values.percentMarkUp}
                              onBlur={e => {
                                handleBlur(e);
                                if (!e.target.value?.includes('%')) {
                                  e.target.value += '%';
                                }
                                setFieldValue('percentMarkUp', `${e.target.value}`);
                              }}
                              onChange={e => {
                                // if value is not blank, then test the regex
                                if (e.target.value === '' || decimalRegExp.test(e.target.value)) {
                                  setFieldValue('percentMarkUp', e.target.value);
                                }
                              }}
                              error={Boolean(touched.percentMarkUp && errors.percentMarkUp)}
                              helperText={touched.percentMarkUp && errors.percentMarkUp}
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SMBSelect
                        onChange={e => {
                          setFieldValue('appraisalAlarmLevel', e.target.value);
                        }}
                        handleClear={() => setFieldValue('appraisalAlarmLevel', '')}
                        name={`appraisalAlarmLevel`}
                        label={'Appraisal Alert Level'}
                        id={`appraisalAlarmLevel`}
                        value={values.appraisalAlarmLevel}
                        onBlur={handleBlur}
                        error={errors && errors.appraisalAlarmLevel ? true : false}
                        variant="outlined"
                      >
                        <MenuItem value={'Low'}>Low</MenuItem>
                        <MenuItem value={'Medium'}>Medium</MenuItem>
                        <MenuItem value={'High'}>High</MenuItem>
                      </SMBSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`additionalUpgradeCap`}>
                        {({ field }) => {
                          return (
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Additional Option Cap"
                              name="additionalUpgradeCap"
                              size="small"
                              value={values.additionalUpgradeCap}
                              onBlur={e => {
                                handleBlur(e);
                                setFieldValue(
                                  'additionalUpgradeCap',
                                  formatMoney(e.target.value, 2)
                                );
                              }}
                              onChange={e => setFieldValue('additionalUpgradeCap', e.target.value)}
                              error={Boolean(
                                touched.additionalUpgradeCap && errors.additionalUpgradeCap
                              )}
                              helperText={
                                touched.additionalUpgradeCap && errors.additionalUpgradeCap
                              }
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Grid container alignItems="flex-start" justifyContent="center" spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <SMBSelect
                            required
                            handleClear={() => setFieldValue('closingAttorneyId', '')}
                            variant="outlined"
                            aria-label="closingAttorneyId"
                            name="closingAttorneyId"
                            value={values.closingAttorneyId}
                            onChange={e => {
                              setFieldValue('closingAttorneyId', parseInt(e.target.value));
                            }}
                            error={errors && errors.closingAttorneyId ? true : false}
                            label="Closing Attorney"
                          >
                            {closingAttorneyIds &&
                              closingAttorneyIds.map((closingAttorney: IAttorneyOffice, index) => {
                                return (
                                  <MenuItem
                                    key={`${closingAttorney.attorneyOfficeId}`}
                                    value={closingAttorney.attorneyOfficeId}
                                  >
                                    {closingAttorney.workplace}
                                  </MenuItem>
                                );
                              })}
                          </SMBSelect>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <SMBSelect
                            required
                            aria-label="compactionPersonId"
                            name="compactionPersonId"
                            variant="outlined"
                            value={values.compactionPersonId}
                            onChange={e => {
                              setFieldValue('compactionPersonId', parseInt(e.target.value));
                            }}
                            error={errors && errors.compactionPersonId ? true : false}
                            handleClear={() => setFieldValue('compactionPersonId', '')}
                            label="Compaction Test User"
                          >
                            {compactionPersonIds &&
                              compactionPersonIds.map((compactionPersonId: IPeople, index) => {
                                return (
                                  <MenuItem
                                    key={`${compactionPersonId.personId}`}
                                    value={compactionPersonId.personId}
                                  >
                                    {compactionPersonId.firstName} {compactionPersonId.lastName} -{' '}
                                    {compactionPersonId.workplace}
                                  </MenuItem>
                                );
                              })}
                          </SMBSelect>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
                <Box mt={1} mb={0.5}>
                  <Divider />
                </Box>

                <ButtonGroup className={classes.saveButtonGroup}>
                  <Button
                    disabled={!dirty || isSubmitting || !isValid}
                    type="submit"
                    startIcon={neighborhoodId ? <Save /> : <Add />}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="inherit"
                    startIcon={<Close />}
                    onClick={() => {
                      history.push('/development/neighborhoods');
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>

                <Grid item xs={12} sm={12}>
                  <Grid container alignItems="flex-start" justifyContent="center" spacing={1}>
                    <Grid item xs={12} sm={12}>
                      <Field name={`platMaps`}>
                        {({ field }) => {
                          return (
                            <FormControl fullWidth variant="outlined" size="small">
                              <Typography variant="h6" className={classes.header}>
                                Plat Maps
                              </Typography>
                              <ImageUploader
                                photoType={'Plat Maps'}
                                images={values.platMaps}
                                setFieldValue={setFieldValue}
                              />
                            </FormControl>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <SalesAndContractsImages
                        neighborhoodId={neighborhoodId}
                        images={values.logos}
                        photoOwnerType={PhotoOwnerType.NeighborhoodLogo}
                        imageTitle={'Logos'}
                        refetchPhotos={refetchLogos}
                      ></SalesAndContractsImages>
                    </Grid>
                    <Grid className={classes.bottomSpacing} item xs={12} sm={12}>
                      <SalesAndContractsImages
                        neighborhoodId={neighborhoodId}
                        images={values.actionPhotographs}
                        photoOwnerType={PhotoOwnerType.NeighborhoodAction}
                        imageTitle={'Action Photographs'}
                        refetchPhotos={refetchActionPhotographs}
                      ></SalesAndContractsImages>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  sliderContainer: {
    height: 300
  },
  internalCommissionRateTitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  mobileAgentTitle: {
    marginBottom: theme.spacing(1)
  },
  emphasis: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  header: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  saveButtonGroup: {
    paddingBottom: theme.spacing(2)
  },
  bottomSpacing: {
    marginBottom: theme.spacing(0.5)
  }
}));
